import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yourmindshift24.com';

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  noindex = false,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector: string, content: string, attr = 'content') => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('="');
        el.setAttribute(attrName, attrVal.replace('"', ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, content);
    };

    const setMetaName = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setMetaProp = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    if (description) setMetaName('description', description);
    if (keywords) setMetaName('keywords', keywords);
    if (noindex) {
      setMetaName('robots', 'noindex, nofollow');
    } else {
      setMetaName('robots', 'index, follow');
    }

    if (canonical) {
      setLink('canonical', `${SITE_URL}${canonical}`);
      setMetaProp('og:url', `${SITE_URL}${canonical}`);
    }

    if (ogTitle || title) setMetaProp('og:title', ogTitle || title || '');
    if (ogDescription || description) setMetaProp('og:description', ogDescription || description || '');
    if (ogImage) setMetaProp('og:image', ogImage);

    // Update last-modified
    setMetaName('last-modified', new Date().toISOString().split('T')[0]);

    // JSON-LD injection
    const scriptId = 'page-seo-jsonld';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) existingScript.remove();

    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(scriptId);
      if (script) script.remove();
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, noindex, JSON.stringify(jsonLd)]);
}

export default useSEO;
